import {
  IconButton, Dialog,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

interface PostAnalysisResult {
  products?: string[];
  brands?: string[];
  companies?: string[];
  industries?: string[];
  mentions?: string[];
  geminiAnalysis?: string;
  tokenUsage?: {
    promptTokens?: number;
    candidatesTokens?: number;
    totalTokens?: number;
  };
}

interface Props {
  onClose: () => void;
  analysis: PostAnalysisResult;
  postId: number;
}

export const PostAnalysisModal = ({ onClose, analysis, postId }: Props) => {
  const renderList = (title: string, items?: string[]) => {
    if (!items || items.length === 0) {
      return null;
    }

    return (
      <div className="mb-4">
        <h3 className="text-md font-semibold mb-2">{title}</h3>
        <ul className="list-disc list-inside">
          {items.map((item) => (
            <li key={item} className="text-sm">{item}</li>
          ))}
        </ul>
      </div>
    );
  };

  // Collect all non-empty sections for two-column layout
  const sections = [
    { title: 'Products', items: analysis.products },
    { title: 'Brands', items: analysis.brands },
    { title: 'Companies', items: analysis.companies },
    { title: 'Industries', items: analysis.industries },
    { title: 'Mentions', items: analysis.mentions },
  ].filter((section) => section.items && section.items.length > 0);

  // Split sections into two columns
  const leftColumn = sections.filter((_, index) => index % 2 === 0);
  const rightColumn = sections.filter((_, index) => index % 2 === 1);

  return (
    <Dialog fullWidth open maxWidth="md" aria-labelledby="post-analysis-dialog">
      <div className="p-6">
        <div className="absolute right-3 top-3">
          <IconButton aria-label="close" onClick={onClose}>
            <CloseIcon color="primary" />
          </IconButton>
        </div>
        <h2 className="text-xl font-semibold mb-4">
          Post Analysis - Post #
          {postId}
        </h2>

        {analysis.geminiAnalysis && (
          <div className="mb-6 p-4 bg-gray-50 rounded">
            <h3 className="text-md font-semibold mb-2">Analysis</h3>
            <p className="text-sm whitespace-pre-wrap">{analysis.geminiAnalysis}</p>
          </div>
        )}

        {sections.length > 0 ? (
          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-4">
              {leftColumn.map((section) => renderList(section.title, section.items))}
            </div>
            <div className="space-y-4">
              {rightColumn.map((section) => renderList(section.title, section.items))}
            </div>
          </div>
        ) : (
          !analysis.geminiAnalysis && (
            <div className="text-sm text-gray-500">
              No analysis data available for this post.
            </div>
          )
        )}

        {analysis.tokenUsage && (
          <div className="mt-6 pt-4 border-t">
            <h3 className="text-sm font-semibold mb-2">Token Usage</h3>
            <div className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
              <div>
                Prompt tokens:
                {analysis.tokenUsage.promptTokens?.toLocaleString() || 'N/A'}
              </div>
              <div>
                Candidates tokens:
                {analysis.tokenUsage.candidatesTokens?.toLocaleString() || 'N/A'}
              </div>
              <div>
                Total tokens:
                {analysis.tokenUsage.totalTokens?.toLocaleString() || 'N/A'}
              </div>
            </div>
          </div>
        )}

        <div className="mt-6 flex justify-end">
          <button
            type="button"
            className="btn-primary"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </Dialog>
  );
};
